import { Ability } from '@casl/ability'
import { Program } from '../../../swagger'
import { FieldValidity } from '../../Interfaces/FieldValidity'
import { ProgramFeeNames } from '../../Programs/ProgramFeeFields'

export interface ProgramFormCardProps {
  /**
   * variant: determines whether we render 'edit' or 'add' variant
   * @type ProgramFormCardVariants
   */
  variant: ProgramFormCardVariants
  /**
   * handleCancel: handles form button cancel event
   * @type () => void
   */
  handleCancel?: () => void
  /**
   * totalEnrolledStudents: Total number of enrolled students
   * @type number | undefined
   */
  totalEnrolledStudents?: number
  /**
   * programDetails: detailed information of a specific program
   * @type Program
   */
  programDetails: Program
  /**
   * handleSave: handles form button save event
   * @type () => void
   */
  handleSave?: () => void
  /**
   * programAbility: `CASL` is a system that enables us to declaratively define and check user permissions,
   * we use it's method for boolean determinations e.g. `Ability.can('edit', 'Program')`
   *  or `programAbility.can('create', 'Program')` for more details visit:
   * https://casl.js.org/v4/en/guide/intro
   * @type Ability
   */
  programAbility?: Ability
  /**
   * setEditForParent: trigger state update at parent component level to set the form to Edit Variant
   * @type (isEdit: boolean) => void
   */
  setEditForParent?: (setEditForParent: boolean) => void
  /**
   * hasEnrollment: determined by the program `enrollments.length > 0 ? true : false`
   * @type boolean
   */
  hasEnrollment?: boolean
  /**
   * updateSelectedTab: updates `selectedTab` state (programDetails) on parent component level.
   * @type boolean
   */
  updateSelectedTab?: (newTab: string) => void
  /**
   * initialProgramDetails: used to persist initial details passed by parent
   * e.g. the user cancels editing we reset back to this initial details
   * @type Program
   */
  initialProgramDetails?: Program
  /**
   * setProgramDetailsForParent: updates state (programDetails) on parent component
   * @type Program
   */
  setProgramDetailsForParent?: (program: Program | undefined) => void
  /**
   * isProgramDetailsEdit: determine if in edit mode used for breadcrumbs and navigation handler
   * @type boolean
   */
  isProgramDetailsEdit?: boolean
  /**
   * hasEnrollmentInvitesWithInProgressStatus: determined by any
   * `enrollmentInvite.status === EnrollmentInvite1StatusEnum.InProgress`
   * a program can not be reverted to Draft if there are invitations still in progress.
   * @type boolean
   */
  hasEnrollmentInvitesWithInProgressStatus?: boolean
  isProgramDetailsLoading?: boolean
}

/** ************************************************************************************************ */

export interface ICommunityInformation {
  communityKey: string
  communityName: string
}

export enum ProgramFormCardVariants {
  AddProgram = 'Add Program',
  EditProgram = 'Edit Program',
}

export interface ProgramFormValidFields {
  type: FieldValidity
  semesterOneStartDate: FieldValidity
  semesterTwoStartDate: FieldValidity
  startTime: FieldValidity
  endTime: FieldValidity
  dayOfTheWeek: FieldValidity
  capacity: FieldValidity
  director: FieldValidity
  tutorName: FieldValidity
  tutorActorId: FieldValidity
  communityName: FieldValidity
  directorPhone: FieldValidity
  tutorModel: FieldValidity
  [ProgramFeeNames.ApplicationFee]: FieldValidity
  [ProgramFeeNames.Tuition]: FieldValidity
  [ProgramFeeNames.EnrollmentFee]: FieldValidity
  [ProgramFeeNames.SupplyFeeFirstStudent]: FieldValidity
  [ProgramFeeNames.SupplyFeeAdditionalStudent]: FieldValidity
  [ProgramFeeNames.LocalFeeFirstStudent]: FieldValidity
  [ProgramFeeNames.LocalFeeAdditionalStudent]: FieldValidity
  [ProgramFeeNames.FacilityFeeFirstStudent]: FieldValidity
  [ProgramFeeNames.FacilityFeeAdditionalStudent]: FieldValidity
  [ProgramFeeNames.MiscFeeFirstStudent]: FieldValidity
  [ProgramFeeNames.MiscFeeAdditionalStudent]: FieldValidity
  [ProgramFeeNames.SubLicensedTutorPercentage]: FieldValidity
  [ProgramFeeNames.SemesterOneLicensingFee]: FieldValidity
  [ProgramFeeNames.DiscountSemesterOneLicensingFee]: FieldValidity
  [ProgramFeeNames.SemesterTwoLicensingFee]: FieldValidity
  [ProgramFeeNames.DiscountSemesterTwoLicensingFee]: FieldValidity
  [ProgramFeeNames.MultiStudentApplicationDiscount]: FieldValidity
  [ProgramFeeNames.InvitationFee]: FieldValidity
  [ProgramFeeNames.DiscountInvitationFee]: FieldValidity
  [ProgramFeeNames.TutorS1FirstStudentLicFee]: FieldValidity
  [ProgramFeeNames.TutorS1AdditionalStudentLicFee]: FieldValidity
  [ProgramFeeNames.TutorS2FirstStudentLicFee]: FieldValidity
  [ProgramFeeNames.TutorS2AdditionalStudentLicFee]: FieldValidity
  [ProgramFeeNames.LcdTutorPercentage]: FieldValidity
}
